import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Copy, CheckCircle2, CircleDashed } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { AppShell, PageTitle } from "@/components/AppShell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { AdminFeedback } from "@/components/AdminFeedback";
import { AdminInvites } from "@/components/AdminInvites";
import { AdminEmailSettings } from "@/components/AdminEmailSettings";
import { AdminSeoLedger } from "@/components/AdminSeoLedger";
import { InviteDialog } from "@/components/InviteDialog";
import { AdminNotifications } from "@/components/AdminNotifications";
import { useAdminDelete, DeleteBar, ConfirmDelete, SelectBox, SelectAll, RowDelete } from "@/components/AdminDelete";
import { useAuth } from "@/context/AuthContext";

const fmt = (d) => format(new Date(d), "MMM d, yyyy HH:mm");

const Table = ({ cols, rows, testid, ids, del, canDelete = () => true, warn = () => "" }) => (
  <>
    {del && <DeleteBar d={del} warnFor={(sel) => sel.map(warn).filter(Boolean).join(" ")} />}
    <ul data-testid={`${testid}-mobile`} className="space-y-3 pb-20 md:hidden">
      {del && rows.length > 0 && <li className="flex items-center gap-2 px-1 text-xs text-muted-foreground"><SelectAll d={del} ids={ids.filter(canDelete)} m />Select all</li>}
      {rows.map((r, i) => (
        <li key={i} data-testid={`${testid}-card`} className="glass rounded-2xl border p-4">
          <div className="flex items-start gap-3">
            {del && canDelete(ids[i]) && <span className="pt-0.5"><SelectBox d={del} id={ids[i]} m /></span>}
            <p className="min-w-0 flex-1 break-all font-medium">{r[0]}</p>
            {del && canDelete(ids[i]) && <RowDelete d={del} id={ids[i]} warn={warn(ids[i])} m />}
          </div>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            {r.slice(1).map((c, j) => (
              <div key={j} className="contents">
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">{cols[j + 1]}</dt>
                <dd className="break-words text-right">{c}</dd>
              </div>
            ))}
          </dl>
        </li>
      ))}
      {!rows.length && <li className="text-sm text-muted-foreground">Nothing yet.</li>}
    </ul>
  <div className="hidden md:block overflow-x-auto rounded-2xl border glass">
    <table data-testid={testid} className="w-full min-w-[560px] text-sm">
      <thead className="bg-secondary/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
        <tr>
          {del && <th className="w-10 px-4 py-3"><SelectAll d={del} ids={ids.filter(canDelete)} /></th>}
          {cols.map((c, i) => <th key={`${c}-${i}`} className="px-4 py-3">{c}</th>)}
          {del && <th className="w-12 px-2 py-3"><span className="sr-only">Delete</span></th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t" data-testid={`${testid}-row`}>
            {del && <td className="px-4 py-3">{canDelete(ids[i]) && <SelectBox d={del} id={ids[i]} />}</td>}
            {r.map((c, j) => <td key={j} className="px-4 py-3">{c}</td>)}
            {del && <td className="px-2 py-1">{canDelete(ids[i]) ? <RowDelete d={del} id={ids[i]} warn={warn(ids[i])} /> : <span className="text-xs text-muted-foreground" data-testid={`${testid}-self`}>You</span>}</td>}
          </tr>
        ))}
      </tbody>
    </table>
    {!rows.length && <p className="p-6 text-sm text-muted-foreground">Nothing yet.</p>}
  </div>
  {del && <ConfirmDelete d={del} />}
  </>
);

const EtsyUsage = ({ u }) => (
  <div data-testid="admin-etsy-usage" className={`rounded-2xl border p-5 ${u.alert === "90" ? "border-red-400/40 bg-red-400/[0.06]" : u.alert === "70" ? "border-[#E9C98B]/40 bg-[#E9C98B]/[0.06]" : "border-white/[0.07] bg-white/[0.03]"}`}>
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <p className="eyebrow">Etsy API budget today (UTC)</p>
      <p data-testid="admin-etsy-usage-count" className="font-mono text-sm">{u.used.toLocaleString()} / {u.limit.toLocaleString()} calls · {u.pct}% · {u.qps} req/sec</p>
    </div>
    <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
      <div className={`h-full origin-left rounded-full ${u.alert === "90" ? "bg-red-400" : u.alert === "70" ? "bg-[#E9C98B]" : "bg-[#5EE6D0]"}`} style={{ transform: `scaleX(${Math.min(1, u.pct / 100)})` }} />
    </div>
    {u.alert && <p data-testid="admin-etsy-usage-alert" className="mt-3 text-sm">Alert: {u.alert}% of today's Etsy budget used. Background attribute loading pauses at 60%, 6-hourly refreshes at 80% and OAuth/imports at 90%; seller pushes and undos keep the rest.{u.pct >= 90 && " New imports are paused now."}</p>}
    <p data-testid="admin-etsy-usage-breakdown" className="mt-3 text-xs text-muted-foreground">
      Pushes/undos {u.by_priority.push_undo} · OAuth/import {u.by_priority.oauth_import} · 6-hourly refresh {u.by_priority.refresh} · stats/attributes {u.by_priority.stats}
      {u.deferred > 0 && <> · {u.deferred} deferred to protect the budget</>}{u.etsy_remaining_today != null && <> · Etsy reports {u.etsy_remaining_today.toLocaleString()} left</>}
    </p>
  </div>
);

const EtsySetup = ({ e }) => (
  <div data-testid="admin-etsy-setup" className="rounded-2xl border glass p-6 space-y-4">
    <p className="flex items-center gap-2 text-sm" data-testid="admin-etsy-status">
      {e.configured ? <CheckCircle2 className="h-4 w-4 text-emerald-300" /> : <CircleDashed className="h-4 w-4 text-[#E9C98B]" />}
      {e.configured ? "Etsy keys configured — real shop connections are live." : "Etsy keys not configured — sellers see \"Etsy connection available soon — try Demo Mode\"."}
    </p>
    <div>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">OAuth callback URL (paste into the Etsy developer portal)</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <code data-testid="admin-callback-url" className="break-all rounded-lg bg-secondary px-3 py-2 font-mono text-sm">{e.callback_url}</code>
        <Button size="sm" variant="outline" className="rounded-full" data-testid="admin-copy-callback"
          onClick={() => navigator.clipboard.writeText(e.callback_url).then(() => toast.success("Copied."))}><Copy className="h-3.5 w-3.5 mr-1" />Copy</Button>
      </div>
    </div>
    <p className="text-sm"><span className="text-muted-foreground">Scopes:</span> <span className="font-mono">{e.scopes}</span></p>
    <p className="text-sm" data-testid="admin-real-shop-count"><span className="text-muted-foreground">Real shops connected:</span> <span className="font-mono">{e.real_shops_connected}/{e.max_real_shops}</span> (personal access)</p>
    {e.usage && <EtsyUsage u={e.usage} />}
    {!e.configured && <p className="text-sm text-muted-foreground">To go live: register a personal app at the Etsy developer portal, then set the backend env vars <code className="font-mono">ETSY_API_KEY</code> (keystring) and <code className="font-mono">ETSY_SHARED_SECRET</code>.</p>}
    <div data-testid="admin-etsy-checklist" className="rounded-2xl border border-white/[0.07] bg-white/[0.03] p-5">
      <p className="eyebrow">Go-live checklist</p>
      <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm">
        <li>Register (free) at <a href="https://www.etsy.com/developers" target="_blank" rel="noreferrer" className="text-[#C2BAFF] underline underline-offset-4">etsy.com/developers</a> with the Etsy account that owns your shop.</li>
        <li>Create a new app and choose <span className="text-foreground">personal access</span> (up to 5 shops).</li>
        <li>Paste the callback URL shown above into the app's callback URLs, exactly as written.</li>
        <li>Copy the app's <span className="text-foreground">keystring</span> and <span className="text-foreground">shared secret</span>.</li>
        <li>Send both to the agent, who adds them through the secure key form. Never paste them in chat or email.</li>
      </ol>
    </div>
    <div data-testid="admin-draft-test-note" className="rounded-2xl border border-[#E9C98B]/30 bg-[#E9C98B]/[0.06] p-5 text-sm text-[#F3DDAE]">
      <p className="font-medium">Draft-listing test first</p>
      <p className="mt-1 text-[#F3DDAE]/80">Per Etsy's API testing policy, run the first real push on a <span className="text-[#F3DDAE]">draft</span> listing with "test" in its title, not on a live listing. Check the change on Etsy, try Undo, then move on to real listings.</p>
    </div>
  </div>
);

export default function Admin() {
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [unread, setUnread] = useState(0);
  const load = () => api.get("/admin/overview").then((r) => setData(r.data)).catch((e) => (e.response?.status === 403 ? setDenied(true) : toast.error(errMsg(e))));
  const { user } = useAuth();
  const delWaitlist = useAdminDelete("waitlist", load);
  const delUsers = useAdminDelete("users", load);
  useEffect(() => {
    load();
    api.get("/admin/feedback").then((r) => setUnread(r.data.unread)).catch(() => {});
  }, []);
  const [invitee, setInvitee] = useState(null);
  const shopCell = (name, url, id) => (
    <span className="flex flex-col">
      <span>{name || "—"}</span>
      {url && <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`waitlist-open-shop-${id}`} className="inline-flex min-h-[44px] items-center text-xs text-[#F3DDAE] underline underline-offset-4">Open shop ↗</a>}
    </span>
  );
  const inviteCell = (w) => {
    if (w.unsubscribed) return <span data-testid={`waitlist-unsubscribed-${w.id}`} className="rounded-full border border-white/15 px-3 py-1 text-xs text-muted-foreground">Unsubscribed</span>;
    if (!w.invited) return <Button size="sm" variant="outline" className="rounded-full" data-testid={`waitlist-invite-${w.id}`} onClick={() => setInvitee(w)}>Invite</Button>;
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid={`waitlist-invited-${w.id}`} className="rounded-full border border-[#E9C98B]/40 bg-[#E9C98B]/10 px-3 py-1 text-xs text-[#F3DDAE]">
          {w.invite_email_sent_at ? `Invited · email sent ${fmt(w.invite_email_sent_at)}` : "Invited"}
        </span>
        <Button size="sm" variant="ghost" className="rounded-full text-xs" data-testid={`waitlist-resend-${w.id}`} onClick={() => setInvitee(w)}>
          {w.invite_email_sent_at ? "Resend invite" : "Send invite email"}
        </Button>
      </div>
    );
  };
  if (denied) return <AppShell><p data-testid="admin-not-authorized" className="font-display text-3xl">Not authorized.</p></AppShell>;
  return (
    <AppShell>
      <PageTitle eyebrow="Founder" title="Admin" testid="admin-title" />
      <InviteDialog entry={invitee} onClose={() => setInvitee(null)} onDone={() => { setInvitee(null); load(); }} />
      {!data ? <div className="h-64 rounded-2xl shimmer" /> : (
        <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "waitlist"}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="waitlist" data-testid="admin-tab-waitlist">Waitlist ({data.waitlist.length})</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users ({data.users.length})</TabsTrigger>
            <TabsTrigger value="etsy" data-testid="admin-tab-etsy">Etsy setup</TabsTrigger>
            <TabsTrigger value="email" data-testid="admin-tab-email">Email log</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="admin-tab-feedback" className="gap-1.5">
              Feedback
              {unread > 0 && <span data-testid="admin-feedback-unread-badge" className="rounded-full bg-[#E9C98B] px-1.5 py-px font-mono text-[10px] text-[#07080F]">{unread}</span>}
            </TabsTrigger>
            <TabsTrigger value="invites" data-testid="admin-tab-invites">Invites</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="seo" data-testid="admin-tab-seo">SEO Ledger</TabsTrigger>
          </TabsList>
          <TabsContent value="waitlist" className="mt-6">
            <Table testid="admin-waitlist-table" cols={["Email", "Shop", "Listings", "Type", "Joined", "Email status", "Invite"]} del={delWaitlist}
              ids={data.waitlist.map((w) => w.id)}
              warn={(id) => { const w = data.waitlist.find((x) => x.id === id); return w?.has_account ? `${w.email} has an account; it will not be deleted (use Users for that).` : ""; }}
              rows={data.waitlist.map((w) => [w.email, shopCell(w.shop, w.shop_url, w.id), w.listing_count ?? "—", w.shop_type, fmt(w.created_at), w.email_status, inviteCell(w)])} />
          </TabsContent>
          <TabsContent value="users" className="mt-6">
            <Table testid="admin-users-table" cols={["Email", "Role", "Shop", "Signed up"]} del={delUsers}
              ids={data.users.map((u) => u.id)} canDelete={(id) => id !== user?.id}
              rows={data.users.map((u) => [u.email, u.role, u.shop ? `${u.shop}${u.shop_kind === "demo" ? " (demo)" : ""}` : "—", fmt(u.created_at)])} />
          </TabsContent>
          <TabsContent value="etsy" className="mt-6"><EtsySetup e={data.etsy} /></TabsContent>
          <TabsContent value="email" className="mt-6">
            <Table testid="admin-email-table" cols={["To", "Result", "Error", "At", "Kind"]}
              rows={data.email_logs.map((l) => [l.to, l.ok ? "sent" : "failed", l.error || "—", fmt(l.at), (l.kind || "—").replace(/_/g, " ")])} />
          </TabsContent>
          <TabsContent value="feedback" className="mt-6"><AdminFeedback onUnread={setUnread} /></TabsContent>
          <TabsContent value="invites" className="mt-6"><AdminInvites onChange={load} /></TabsContent>
          <TabsContent value="settings" className="mt-6"><AdminEmailSettings /><AdminNotifications /></TabsContent>
          <TabsContent value="seo" className="mt-6"><AdminSeoLedger /></TabsContent>
        </Tabs>
      )}
    </AppShell>
  );
}
